import React, { useState, useEffect } from "react";
import { Booking, Driver, Car } from "../components/models/booking";
import { getBookingByBookingId } from "../components/services/BookingService";
import { getDriver } from "../components/services/driverService";
import { getCar } from "../components/services/carsService";
import { convertUTCToIST, formatDate, } from "../components/booking/components/common/utils";
import { useParams } from "react-router-dom";
import '../styles/commonStyle.css';
import EstimatedFareReadOnly from "../components/booking/common/EstimatedFareReadOnly";
import {
  Person, Phone, Place, CalendarToday, DirectionsCar, People, Luggage, ElectricCar, CheckCircle, Schedule,
  Cancel, PlayArrow, Flag, Navigation, CurrencyRupee
} from "@mui/icons-material";


const BookingRequest: React.FC = () => {
  const { bookingId } = useParams();

  const [booking, setBooking] = useState<Booking | null>(null);
  const [driver, setDriver] = useState<Driver>();
  const [car, setCar] = useState<Car>();
  const [loading, setLoading] = useState<boolean>(false);

  const getBooking = async () => {
    setLoading(true);
    const booking = await getBookingByBookingId(bookingId);
    if (booking) {
      setBooking(booking);
      const driver = getDriver(booking.driverId);
      const car = getCar(booking.carId);
      if (driver) setDriver(driver);
      if (car) setCar(car);
    }
    setLoading(false);
  };

  useEffect(() => {
    getBooking();
  }, [booking?.driverId]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "NEW":
        return <Schedule fontSize="small" />;
      case "CONFIRMED":
        return <CheckCircle fontSize="small" />;
      case "STARTED":
        return <PlayArrow fontSize="small" />;
      case "FINISHED":
        return <Flag fontSize="small" />;
      case "CANCELLED":
        return <Cancel fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };
  const getIconColor = (status: string) => {
    switch (status) {
      case "NEW":
      case "CONFIRMED":
      case "STARTED":
      case "FINISHED":
        return "success";
      case "CANCELLED":
        return "secondary";
      default:
        return "dark";
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "NEW":
        return "warning";
      case "CONFIRMED":
      case "STARTED":
      case "FINISHED":
        return "success-green"; // custom
      case "CANCELLED":
        return "secondary";
      default:
        return "dark";
    }
  };

  const getMsg = () => {
    switch (booking?.status) {
      case "NEW":
        return "Your booking request has been accepted. We will check the availability and send a confirmation to your WhatsApp number shortly.";
      case "CONFIRMED":
        return "Your booking is confirmed.";
      case "STARTED":
        return "Your ride has started.";
      case "FINISHED":
        return "Your ride has been completed successfully.";
      case "CANCELLED":
        return "Your booking has been cancelled.";
      default:
        return "";
    }
  };

  if (loading) {
    return (
      <div className="container-fluid bg-light py-4 min-vh-100">
        <div className="card shadow-sm border-0 mx-auto" style={{ maxWidth: 900 }}>
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3"></div>
            <h6 className="text-muted">Loading booking details...</h6>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="container-fluid bg-light py-4 min-vh-100">
        <div className="card shadow-sm border-1 mx-auto" style={{ maxWidth: 900 }}>
          <div className="card-body text-center py-5">
            <h6 className="text-muted">No Booking Found</h6>
          </div>
        </div>
      </div>
    );
  }

  const personalInfo = [
    { label: "Name ", value: booking.name, icon: <Person /> },
    { label: "Phone", value: booking.phone, icon: <Phone /> },
  ];

  const tripInfo = [
    { label: "From ", value: booking.fromAddress, icon: <Place /> },
    { label: "To ", value: booking.toAddress, icon: <Place /> },
    { label: "Trip Date ", value: convertUTCToIST(booking.bookingDate), icon: <CalendarToday />, },
    { label: "Created", value: formatDate(booking.createdDate), icon: <CalendarToday />, },
    { label: "Return", value: booking.returnRide ? "Yes" : "No", icon: <DirectionsCar /> },
  ];

  const vehicleInfo = [
    { label: "Driver", value: driver?.name, icon: <Person />, },
    { label: "Car Number", value: car?.carNumber, icon: <ElectricCar />, },
    { label: "Car Model", value: booking.selectedCarModel || "BYD E6", icon: <ElectricCar />, },
    { label: "Passengers", value: booking.numberOfPassengers, icon: <People />, },
    { label: "Big Bags", value: booking.numberOfBigBags, icon: <Luggage />, },
  ];

  return (
    <div className="container-fluid py-4 min-vh-100">
      <div className="mx-auto" style={{ maxWidth: 900 }}>

        <div className="card shadow-sm border-1 p-1 rounded-3">
          <div className="card-body text-center pb-2 mt-2">
            <h4 className="text-success">Booking Summary</h4>
            <h6 className="fw-bold">BookingId : {booking.bookingId}</h6>
          </div>
          <hr className="mt-0 mb-3" />

          <div className={`alert alert-${getStatusColor(booking.status)} d-flex align-items-center text-start mx-2`}>
            <div className="me-2">{getStatusIcon(booking.status)}</div>
            <div className="">{getMsg()}</div>
          </div>


          <div className="card-body pb-2 border-bottom">
            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center">
              <div className={`bg-${getIconColor(booking.status)} bg-opacity-10 rounded-circle p-2 me-2`}>
                <Person className={`text-${getIconColor(booking.status)}`} />
              </div>
               Customer Details :
            </h6>

            <div className="row g-2">
              {personalInfo.map((item, idx) => (
                <div className="col-12 col-md-6" key={idx}>
                  <div className="row py-2">
                    <div className="col-4 d-flex align-items-center text-muted small fw-bold text-uppercase">
                      {item.icon}
                      <span className="ms-1 text-start">{item.label}:</span>
                    </div>
                    <div className="col-8 small text-dark text-start">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>


          <div className="card-body pb-2 border-bottom">
            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center">
              <div className={`bg-${getIconColor(booking.status)} bg-opacity-10 rounded-circle p-2 me-2`}>
                <Navigation className={`text-${getIconColor(booking.status)}`} />
              </div>
               Trip Details :
            </h6>

            <div className="row g-2">
              {tripInfo.map((item, idx) => (
                <div className="col-12 col-md-6" key={idx}>
                  <div className="row py-2">
                    <div className="col-4 d-flex align-items-center text-muted small fw-bold text-uppercase">
                      {item.icon}
                      <span className="ms-1 text-start">{item.label}</span>
                    </div>
                    <div className="col-8 small text-dark text-start">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>


          <div className="card-body pb-2 border-bottom">
            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center">
              <div className={`bg-${getIconColor(booking.status)} bg-opacity-10 rounded-circle p-2 me-2`}>
                <ElectricCar className={`text-${getIconColor(booking.status)}`} />
              </div>
               Vehicle & Driver Details :
            </h6>

            <div className="row g-2">
              {vehicleInfo.map((item, idx) => (
                <div className="col-12 col-md-6" key={idx}>
                  <div className="row py-2">
                    <div className="col-6 d-flex align-items-center text-muted small fw-bold text-uppercase">
                      {item.icon}
                      <span className="ms-1 text-start">{item.label} :</span>
                    </div>
                    <div className="col-6 small text-dark text-start">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card-body">
            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center">
              <div className={`bg-${getIconColor(booking.status)} bg-opacity-10 rounded-circle p-2 me-2`}>
                <CurrencyRupee className={`text-${getIconColor(booking.status)}`} />
              </div>
               Fare Breakdown :
          </h6>
            <EstimatedFareReadOnly booking={booking} />
          </div>
        </div>
      </div>
    </div>

  );
};

export default BookingRequest;
